// @ts-nocheck
/**
 * New Flash Message — redesigned to obd3-gui pattern (ScrollPage + PageHeader
 * + Card + shadcn Select/Label/Textarea). Module list fetched at mount;
 * Action / Type options from FLASH_MESSAGE_ACTION / FLASH_MESSAGE_TYPE.
 *
 * v1 quirk preserved verbatim: `onSuccess` navigates to
 * `/${API_FLASH_MESSAGE_LIST || ""}` — that's a bug (API URL used as a
 * route path), kept as-is.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { ArrowLeft, Loader2, Megaphone, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { postApiCall, getApiCall } from '@/service/httpCall';
import {
  API_FLASH_MESSAGE_CREATE,
  API_MODULE_LIST,
  API_FLASH_MESSAGE_LIST,
} from '@/service/url';
import {
  FLASH_MESSAGE_ACTION,
  FLASH_MESSAGE_TYPE,
  ROUTES,
} from '@/utilities/constant';

export default function FlashMessageCreate() {
  const navigate = useNavigate();
  const [buttonDisable, setButtonDisable] = useState(false);
  const [moduleList, setModuleList] = useState<any[]>([]);
  const [selectedModule, setSelectedModule] = useState('');
  const [action, setAction] = useState('');
  const [type, setType] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    getApiCall(API_MODULE_LIST, {})
      .then((resp: any) => setModuleList(resp || []))
      .catch((err: any) => console.log(err));
  }, []);

  function validateForm() {
    if (!selectedModule) {
      toast.warning('Module is required');
      return false;
    }
    if (!action) {
      toast.warning('Action is required');
      return false;
    }
    if (!message) {
      toast.warning('Message is required');
      return false;
    }
    return true;
  }

  function submitForm(e: any) {
    e.preventDefault();
    if (!validateForm()) return;
    setButtonDisable(true);
    postApiCall(API_FLASH_MESSAGE_CREATE, {
      module_name: selectedModule,
      action,
      message,
      type,
    })
      .then(() => {
        toast.success('Flash message created successfully');
        // v1 quirk preserved verbatim
        setTimeout(() => navigate(`/${API_FLASH_MESSAGE_LIST || ''}`), 1000);
      })
      .catch((err: any) => {
        setButtonDisable(false);
        toast.error(friendlyApiError(err) || 'Failed to create flash message');
      });
  }

  return (
    <ScrollPage>
      <PageHeader
        title="New Flash Message"
        description="Attach a module-scoped inline note or flash message shown across the app."
        actions={
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.FLASH_MESSAGE.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      <form className="space-y-4" onSubmit={submitForm}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Megaphone className="h-4 w-4 text-primary" />
              Message details
            </CardTitle>
            <CardDescription>
              Scope the message to a module + action, pick a type, then write the body.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="module">
                  Module <span className="text-destructive">*</span>
                </Label>
                <Select value={selectedModule} onValueChange={setSelectedModule}>
                  <SelectTrigger id="module">
                    <SelectValue placeholder="Select module" />
                  </SelectTrigger>
                  <SelectContent>
                    {moduleList.map((item: any) => (
                      <SelectItem key={item.module_name} value={item.module_name}>
                        {item.module_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="action">
                  Action <span className="text-destructive">*</span>
                </Label>
                <Select value={action} onValueChange={setAction}>
                  <SelectTrigger id="action">
                    <SelectValue placeholder="Select action" />
                  </SelectTrigger>
                  <SelectContent>
                    {FLASH_MESSAGE_ACTION.map((item: string) => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">
                  Type <span className="text-destructive">*</span>
                </Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger id="type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {FLASH_MESSAGE_TYPE.map((item: string) => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">
                Message <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="message"
                rows={4}
                placeholder="Enter Flash / Note message…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="ghost">
            <Link to={`/${ROUTES.FLASH_MESSAGE.LIST}`}>Cancel</Link>
          </Button>
          <Button type="submit" disabled={buttonDisable}>
            {buttonDisable ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {buttonDisable ? 'Adding…' : 'Add flash message'}
          </Button>
        </div>
      </form>
    </ScrollPage>
  );
}
